import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { PermissionStatusBadge, ClimateRiskBadge, VisibilityBadge } from "../components/Badges";
import {
  Sprout, Leaf, Users, ShieldCheck, Dna, Mic2, Sun, MapPin, PlusCircle, AlertTriangle, ArrowRight,
  MapPinned,
} from "lucide-react";

const DOMAINS = [
  {
    key: "ecological",
    title: "Ecological Intelligence",
    icon: MapPinned,
    items: ["Site characteristics", "Species associations", "Environmental conditions"],
  },
  {
    key: "genetic",
    title: "Genetic Intelligence",
    icon: Dna,
    items: ["Provenance", "Population diversity", "Germplasm information"],
  },
  {
    key: "cultural",
    title: "Cultural Intelligence",
    icon: ShieldCheck,
    items: ["Traditional Owner authority", "Cultural permissions", "Knowledge governance"],
  },
  {
    key: "climate",
    title: "Climate Intelligence",
    icon: Sun,
    items: ["Environmental change indicators", "Climate projections", "Adaptation opportunities"],
  },
];

function Kpi({ icon: Icon, label, value, hint, testid, accent }) {
  return (
    <Card className="p-6 border-[#E6E2D8] shadow-sm hover:shadow-md transition-all duration-200 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8A857D]">{label}</div>
          <div className="font-display text-3xl font-bold tracking-tight text-[#2C2A28] mt-2">{value}</div>
          {hint && <div className="text-[11.5px] text-[#5C5854] mt-2">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md border ${accent || "border-[#E6E2D8] bg-[#F4F1EA]"} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/collections/stats")).data,
  });

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <header className="space-y-7" data-testid="dashboard-hero">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="font-display text-[34px] sm:text-[40px] leading-none tracking-[0.18em] font-bold text-[#8B5A2B]" data-testid="hero-acronym">SPIRIT</span>
              <span className="h-6 w-px bg-[#E6E2D8] mx-2" />
              <span className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#8A857D]">Provenance Intelligence Platform</span>
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#2C2A28] leading-[1.05]">
              Seed Provenance Intelligence and Restoration Information Technology
            </h1>
            <p className="text-[15px] text-[#2C2A28] mt-5 leading-relaxed max-w-2xl">
              An integrated platform for the collection, management and analysis of ecological, genetic, cultural and
              climate datasets associated with native seed resources.
            </p>
          </div>
          <Button
            onClick={() => navigate("/collections/new")}
            className="bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7] gap-2 shrink-0"
            data-testid="dashboard-new-collection"
          >
            <PlusCircle className="w-4 h-4" strokeWidth={1.8} /> New collection
          </Button>
        </div>

        <div className="sk-divider" />

        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Four Integrated Knowledge Domains</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5 sk-stagger" data-testid="knowledge-domains">
            {DOMAINS.map((d) => (
              <Card key={d.key} className="p-5 border-[#E6E2D8] shadow-sm bg-white hover:shadow-md transition-all duration-200" data-testid={`domain-${d.key}`}>
                <div className="w-10 h-10 rounded-md bg-[#F4F1EA] border border-[#E6E2D8] flex items-center justify-center">
                  <d.icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-[15px] font-semibold tracking-tight text-[#2C2A28] mt-4">{d.title}</h3>
                <ul className="mt-3 space-y-1.5 text-[12.5px] leading-relaxed text-[#5C5854]">
                  {d.items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="mt-[7px] w-1 h-1 rounded-full bg-[#8B5A2B] shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          <p className="text-[13.5px] text-[#2C2A28] mt-6 leading-relaxed max-w-3xl italic font-serif-display">
            SPIRIT integrates these knowledge domains into a single provenance intelligence framework for native seed systems.
          </p>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 sk-stagger">
        <Kpi icon={Sprout} label="Total collections" value={isLoading ? "—" : data?.total_collections ?? 0} testid="kpi-total" />
        <Kpi icon={Leaf} label="Species recorded" value={isLoading ? "—" : data?.species_recorded ?? 0} testid="kpi-species" />
        <Kpi icon={Users} label="TO groups" value={isLoading ? "—" : data?.to_groups_involved ?? 0} hint="Traditional Owner groups involved" testid="kpi-to-groups" />
        <Kpi icon={ShieldCheck} label="Permissions complete" value={isLoading ? "—" : data?.permissions_complete ?? 0} hint="Approved cultural permissions" testid="kpi-permissions" />
        <Kpi icon={Dna} label="DNA samples" value={isLoading ? "—" : data?.dna_samples_taken ?? 0} hint="Collections with samples taken" testid="kpi-dna" />
        <Kpi icon={Mic2} label="Audio observations" value={isLoading ? "—" : data?.audio_collections ?? 0} hint="Collections with audio recordings" testid="kpi-audio" />
        <Kpi icon={Sun} label="Climate assessed" value={isLoading ? "—" : data?.climate_assessments ?? 0} hint="With climate risk score" testid="kpi-climate" />
        <Kpi icon={MapPin} label="On the map" value={isLoading ? "—" : data?.recent_collections?.filter(c => c.ecological?.latitude)?.length ?? 0} hint="Geolocated collections" testid="kpi-map" />
      </section>

      <section className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white" data-testid="recent-collections">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8A857D]">Recent collections</div>
              <h2 className="font-display text-xl font-semibold tracking-tight text-[#2C2A28] mt-1">Latest activity</h2>
            </div>
            <Link to="/collections" className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] hover:underline flex items-center gap-1" data-testid="link-all-collections">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {(!data?.recent_collections?.length) ? (
            <div className="rounded-md border border-dashed border-[#E6E2D8] p-8 text-center">
              <Sprout className="w-8 h-8 mx-auto text-[#8B5A2B]" strokeWidth={1.4} />
              <p className="font-display text-lg font-medium mt-3 text-[#2C2A28]">No collections yet</p>
              <p className="text-[13px] text-[#5C5854] mt-1">Start by recording your first seed collection.</p>
              <Button onClick={() => navigate("/collections/new")} className="mt-4 bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7]" data-testid="empty-new-collection">
                Record a collection
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#E6E2D8]">
              {data.recent_collections.map((c) => (
                <Link
                  key={c.id}
                  to={`/collections/${c.id}`}
                  data-testid={`recent-row-${c.id}`}
                  className="grid grid-cols-12 gap-3 items-center py-3 sk-row-hover -mx-2 px-2 rounded-md transition-colors"
                >
                  <div className="col-span-4">
                    <div className="font-mono-data text-[11px] text-[#8A857D]">{c.collection_id}</div>
                    <div className="text-[14px] font-medium text-[#2C2A28] italic font-serif-display">{c.species_scientific_name || "Unidentified species"}</div>
                    <div className="text-[12px] text-[#5C5854]">{c.species_common_name}</div>
                  </div>
                  <div className="col-span-3 text-[12.5px] text-[#5C5854]">
                    <div className="truncate">{c.project_name || "—"}</div>
                    <div className="text-[11px] text-[#8A857D]">{c.date_collected || "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <PermissionStatusBadge status={c.cultural?.cultural_permission_status} />
                  </div>
                  <div className="col-span-2">
                    <ClimateRiskBadge score={c.climate?.climate_risk_score} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <VisibilityBadge level={c.visibility_level} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white" data-testid="priority-alerts">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8A857D]">Priority alerts</div>
          <h2 className="font-display text-xl font-semibold tracking-tight text-[#2C2A28] mt-1">What needs attention</h2>
          <div className="mt-5 space-y-3">
            {(data?.alerts || []).length === 0 && (
              <p className="text-[13px] text-[#5C5854]">No alerts. Nothing urgent at the moment.</p>
            )}
            {(data?.alerts || []).map((a, i) => (
              <div
                key={i}
                data-testid={`alert-${a.level}-${i}`}
                className={`rounded-md border px-4 py-3 flex items-start gap-2 ${
                  a.level === "danger"
                    ? "border-[#C47F4B] bg-[#FFF1E5] text-[#B45B3E]"
                    : a.level === "warning"
                    ? "border-[#D08C38] bg-[#FFF7E8] text-[#9B6716]"
                    : "border-[#E6E2D8] bg-[#F4F1EA] text-[#5C5854]"
                }`}
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
                <span className="text-[13px] leading-relaxed">{a.message}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}
